import React, { ForwardRefExoticComponent, RefAttributes, useEffect, useRef } from 'react'
import { BtnV2, l } from '@manychat/manyui'

import { FieldObtainedMessage } from 'apps/aiAutomation/components/Playground/components/FieldObtainedMessage'
import {
  MessageBubble,
  MessageType,
} from 'apps/aiAutomation/components/Playground/components/MessageBubble'
import { ConversationStatus, Message } from 'apps/aiAutomation/components/Playground/types'

import { SystemMessage } from '../SystemMessage'

import { TypingDots } from './components/TypingDots'

import cm from './styles.module.css'

interface ChatProps {
  messages: Message[]
  conversationStatus: ConversationStatus

  ConversationFinishedControls: ForwardRefExoticComponent<RefAttributes<HTMLLIElement>>

  onRestart: () => void
}

export const Chat = ({
  messages,
  conversationStatus,
  onRestart,
  ConversationFinishedControls,
}: ChatProps) => {
  const lastMessageRef = useRef<HTMLLIElement | null>(null)

  const isFinished = conversationStatus === ConversationStatus.FINISHED
  const isTyping = conversationStatus === ConversationStatus.ONGOING_TYPING

  useEffect(() => {
    lastMessageRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, isFinished, isTyping])

  return (
    <div className={cm.wrapper}>
      {!isFinished && (
        <BtnV2
          onClick={onRestart}
          className={cm.restartButton}
          onlyIcon="Retry"
          ariaLabel={l.translate('Restart preview')}
        />
      )}

      <ul className={cm.chatBox}>
        <li className={cm.chatBox__spacer} />
        {messages.map((message, index) => {
          const isLast = messages.length - 1 === index && !isFinished && !isTyping

          if (message.type === 'text') {
            return (
              <MessageBubble
                key={index}
                ref={isLast ? lastMessageRef : null}
                text={message.data.text}
                type={message.author === 'user' ? MessageType.SENT : MessageType.RECEIVED}
              />
            )
          }

          if (message.type === 'system') {
            return (
              <SystemMessage
                key={index}
                ref={isLast ? lastMessageRef : null}
                text={message.data.text}
              />
            )
          }

          if (message.type === 'fieldObtained') {
            return (
              <FieldObtainedMessage
                key={index}
                ref={isLast ? lastMessageRef : null}
                fieldId={message.data.field_id}
                value={message.data.field_value}
              />
            )
          }

          return <pre key={index}>{JSON.stringify(message, null, 2)}</pre>
        })}

        {isTyping && <TypingDots ref={lastMessageRef} />}

        {isFinished && <ConversationFinishedControls ref={lastMessageRef} />}
      </ul>
    </div>
  )
}
